import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipelinesApi } from '../services/pipelinesApi';
import type {
  CreateTicketStageRequest,
  UpdateTicketStageRequest,
  ReorderTicketStagesRequest
} from '@troia-v3/types';
import { toast } from 'sonner';

export const STAGE_KEYS = {
  all: ['ticket-stages'] as const,
  byPipeline: (pipelineId: string) => [...STAGE_KEYS.all, 'by-pipeline', pipelineId] as const,
  details: () => [...STAGE_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...STAGE_KEYS.details(), id] as const,
};

export const useStagesByPipelineId = (pipelineId?: string) => {
  return useQuery({
    queryKey: STAGE_KEYS.byPipeline(pipelineId!),
    queryFn: () => pipelinesApi.getStagesByPipelineId(pipelineId!),
    enabled: !!pipelineId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTicketStageRequest) => pipelinesApi.createStage(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.all });
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.byPipeline(result.pipelineId) });
      toast.success('Etapa criada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar etapa');
    },
  });
};

export const useUpdateStage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTicketStageRequest }) =>
      pipelinesApi.updateStage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.all });
      toast.success('Etapa atualizada com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar etapa');
    },
  });
};

export const useDeleteStageWithValidation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pipelinesApi.deleteStageWithValidation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.all });
      toast.success('Etapa excluida com sucesso');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao excluir etapa');
    },
  });
};

export const useReorderStages = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReorderTicketStagesRequest) => pipelinesApi.reorderStages(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STAGE_KEYS.all });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao reordenar etapas');
    },
  });
};
